import { loadPatientOptions } from '@api/loadOptions'
import { appointmentTypes } from '@data'
import { useAppointment } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import { DateInputField, FormButton, Modal } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { getError, parseIsoToLocalDate, showToast } from '@util'
import moment from 'moment'
import { useEffect, useMemo } from 'react'

import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

/** Calendar event shape when opened from calendar click (for edit / prefill) */
export type CalendarEventDefaultData = {
  id: string
  resourceId: string
  title?: string
  meta?: {
    attributes?: { appointment_at?: string; start_time?: string; end_time?: string; appointment_type?: string }
    doctorName?: string
    patientName?: string
    relationships?: { patient?: { data?: { id: string } }; staff?: { data?: { id: string } } }
  }
  start?: Date
}

type Props = {
  onClose: () => void
  id?: string
  defaultData?: CalendarEventDefaultData | unknown
}

const appointmentSchema = yup.object({
  doctor_id: yup.string().required('Doctor is required'),
  patient_id: yup.string().required('Patient is required'),
  start_time: yup.string().required('Time slot is required'),
  end_time: yup.string().required('Time slot is required'),
  appointment_type: yup.string().required('Appointment type is required'),
  appointment_at: yup.string().required('Appointment date is required')
})

export const CreateAppointment = ({ onClose, id, defaultData }: Props) => {
  const methods = useForm({ resolver: yupResolver(appointmentSchema) })
  const { closeModal } = useModalStore()
  const { watch, setValue, reset, formState, handleSubmit } = methods
  const watchAppointmentAt = watch('appointment_at')
  const watchDoctorId = watch('doctor_id')
  const watchStartTime = watch('start_time')
  const watchEndTime = watch('end_time')

  const { availableDoctors, bookAppointment, updateAppointment, timeslots, timeslotIsLoading } = useAppointment({
    appointment_at: moment(watchAppointmentAt).format('YYYY-MM-DD'),
    date: moment(watchAppointmentAt).format('YYYY-MM-DD'),
    staff_id: watchDoctorId
  })

  const slotList = timeslots?.timeslots ?? []
  const hasDateAndDoctor = watchAppointmentAt && watchDoctorId
  const eventData = defaultData as CalendarEventDefaultData | undefined
  const isEdit = !!id
  const isDateInPast = !!watchAppointmentAt && moment(watchAppointmentAt).isBefore(moment(), 'day')

  const defaultFormValues = useMemo(() => {
    if (!eventData?.meta) return undefined
    const attrs = eventData.meta.attributes
    const patientId = eventData.meta.relationships?.patient?.data?.id
    const appointmentAt = attrs?.appointment_at || (eventData.start ? moment(eventData.start).format('YYYY-MM-DD') : '')
    return {
      appointment_at: appointmentAt || '',
      doctor_id: eventData.resourceId || '',
      end_time: (attrs?.end_time && moment(parseIsoToLocalDate(attrs.end_time)).format('HH:mm')) || '',
      patient_id: patientId || '',
      appointment_type: eventData.title || attrs?.appointment_type || '',
      start_time: (attrs?.start_time && moment(parseIsoToLocalDate(attrs.start_time)).format('HH:mm')) || ''
    }
  }, [eventData?.id, eventData?.meta, eventData?.resourceId, eventData?.start, eventData?.title])

  useEffect(() => {
    if (!defaultFormValues) return
    reset(defaultFormValues)
  }, [defaultFormValues, reset])

  useEffect(() => {
    if (!isDateInPast) return
    if (!watchStartTime) setValue('start_time', '09:00', { shouldValidate: true })
    if (!watchEndTime) setValue('end_time', '17:00', { shouldValidate: true })
  }, [isDateInPast, watchStartTime, watchEndTime, setValue])

  const doctorOptions = useMemo(() => {
    const list =
      availableDoctors?.map(doctor => ({
        label: [doctor.attributes.first_name, doctor.attributes.last_name].filter(Boolean).join(' ').trim() || '—',
        value: doctor.id
      })) ?? []
    if (isEdit && eventData?.resourceId && !list.some(o => o.value === eventData.resourceId))
      return [{ label: eventData.meta?.doctorName ?? 'Selected doctor', value: eventData.resourceId }, ...list]
    return list
  }, [availableDoctors, isEdit, eventData?.resourceId, eventData?.meta?.doctorName])

  const patientDefaultOptions = useMemo(() => {
    if (!isEdit || !eventData?.meta?.patientName) return []
    const pid = eventData.meta.relationships?.patient?.data?.id
    if (!pid) return []
    return [{ label: eventData.meta.patientName, value: pid }]
  }, [isEdit, eventData?.meta?.patientName, eventData?.meta?.relationships?.patient?.data?.id])

  const onSubmit = async (formData: yup.InferType<typeof appointmentSchema>) => {
    const { appointment_at, doctor_id, end_time, patient_id, appointment_type, start_time } = formData
    const payload = {
      data: {
        attributes: {
          appointment_at: moment(appointment_at).format('YYYY-MM-DD'),
          end_time,
          appointment_type,
          start_time
        },
        relationships: {
          staff: { data: { id: doctor_id, type: 'staff' } },
          patient: { data: { id: patient_id, type: 'patient' } }
        },
        type: 'appointment'
      }
    }
    try {
      if (isEdit && id) {
        await updateAppointment({ data: payload, id })
        showToast('Appointment updated successfully')
        closeModal(ADMIN_MODAL_TYPE.CALENDAR_DETAILS)
      } else {
        await bookAppointment(payload)
        showToast('Appointment booked successfully')
      }
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title={id ? 'Edit Appointment' : 'Create Appointment'}>
        <form
          className="flex flex-col  overflow-auto w-full min-w-0 md:max-w-[550px]"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="overflow-auto flex flex-col gap-4 p-6">
            <div className="">
              <DateInputField label="Date" name="appointment_at" placeholder="Select appointment date" />
            </div>
            <div className="">
              <FormSelect
                defaultOptions
                getOptionLabel={({ label }) => label}
                getOptionValue={({ value }) => value}
                label="Doctor"
                name="doctor_id"
                options={doctorOptions}
                value={doctorOptions.find(({ value }) => value === watchDoctorId)}
              />
            </div>
            <Controller
              control={methods.control}
              name="patient_id"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  borderRadius={'100px'}
                  defaultValue={patientDefaultOptions.length > 0 ? patientDefaultOptions[0] : undefined}
                  isAsync
                  isDisabled={isEdit}
                  label="Patient"
                  loadOptions={loadPatientOptions}
                  onChange={(selectedOption: any) => onChange(selectedOption?.value || '')}
                  options={patientDefaultOptions}
                  value={patientDefaultOptions.find(({ value }) => value === watch('patient_id'))}
                />
              )}
            />
            {/* <FormSelect
              defaultOptions={patientDefaultOptions.length > 0 ? patientDefaultOptions : undefined}
              defaultValue={patientDefaultOptions.length > 0 ? patientDefaultOptions[0] : undefined}
              getOptionLabel={({ label }) => label}
              getOptionValue={({ value }) => value}
              isAsync
              isDisabled={isEdit}
              label="Patient"
              loadOptions={loadPatientOptions}
              name="patient_id"
            /> */}
            <div className="">
              <FormSelect
                label="Appointment Type"
                name="appointment_type"
                options={appointmentTypes}
                placeholder="Select appointment type"
                value={appointmentTypes.find(({ value }) => value === watch('appointment_type'))}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Time slot</label>
              {!hasDateAndDoctor ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Select a date and doctor to see available time slots.
                </p>
              ) : timeslotIsLoading ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  Loading time slots…
                </p>
              ) : isDateInPast ? (
                <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  <span>
                    <strong>Start:</strong> {watchStartTime || '—'}
                  </span>
                  <span>
                    <strong>End:</strong> {watchEndTime || '—'}
                  </span>
                </div>
              ) : slotList.length === 0 ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  No available time slots for this date and doctor.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {slotList.map((slot, idx) => {
                    const isObjectSlot =
                      typeof slot === 'object' && slot !== null && 'startTime' in slot && 'endTime' in slot
                    const startTime = isObjectSlot
                      ? (slot as { startTime: string; endTime: string }).startTime
                      : (slot as string)
                    const endTime = isObjectSlot
                      ? (slot as { startTime: string; endTime: string }).endTime
                      : (slot as string)
                    const label = isObjectSlot ? `${startTime} – ${endTime}` : String(slot)
                    const isSelected = watchStartTime === startTime && watchEndTime === endTime
                    return (
                      <button
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/10'
                        }`}
                        key={idx}
                        onClick={() => {
                          setValue('start_time', startTime, { shouldValidate: true })
                          setValue('end_time', endTime, { shouldValidate: true })
                        }}
                        type="button"
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
          <FormButton
            isLoading={formState.isSubmitting}
            onClose={onClose}
            submit={id ? 'Edit Appointment' : 'Create Appointment'}
          />
        </form>
      </Modal>
    </FormProvider>
  )
}
